import React, { useState, useCallback } from 'react';
import {
  getBezierPath,
  EdgeLabelRenderer,
  BaseEdge,
  useReactFlow,
} from 'reactflow';
import './BOMEdge.css';

const BOMEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
  source,
  target,
}) => {
  const { setEdges, getNode } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);
  const [tempQuantity, setTempQuantity] = useState(data?.quantity || 1);

  // Check if source node is phantom
  const sourceNode = getNode(source);
  const isPhantomPassthrough = sourceNode?.data?.isPhantom || false;

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    curvature: 0.25,
  });

  const handleQuantitySubmit = useCallback(() => {
    const newQty = parseFloat(tempQuantity);
    if (!isNaN(newQty) && newQty >= 0) {
      setEdges((edges) =>
        edges.map((edge) => {
          if (edge.id === id) {
            return {
              ...edge,
              data: {
                ...edge.data,
                quantity: newQty,
              },
            };
          }
          return edge;
        })
      );
    }
    setIsEditing(false);
  }, [id, tempQuantity, setEdges]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleQuantitySubmit();
    } else if (e.key === 'Escape') {
      setTempQuantity(data?.quantity || 1);
      setIsEditing(false);
    }
  }, [handleQuantitySubmit, data?.quantity]);

  const handleDelete = useCallback((e) => {
    e.stopPropagation();
    setEdges((edges) => edges.filter((edge) => edge.id !== id));
  }, [id, setEdges]);

  // Format quantity for display
  const formatQuantity = (qty) => {
    if (qty === undefined || qty === null) return '1';
    const num = parseFloat(qty);
    if (Number.isInteger(num)) return num.toString();
    // Show up to 6 decimal places, but trim trailing zeros
    return num.toFixed(6).replace(/\.?0+$/, '');
  };

  return (
    <>
      <BaseEdge
        path={edgePath}
        className={`bom-edge ${selected ? 'selected' : ''} ${isPhantomPassthrough ? 'phantom' : ''}`}
        style={{
          strokeWidth: selected ? 3 : 2,
          stroke: isPhantomPassthrough ? '#f59e0b' : (selected ? '#60a5fa' : '#475569'),
          strokeDasharray: isPhantomPassthrough ? '8 4' : 'none',
        }}
        markerEnd={`url(#arrow-${isPhantomPassthrough ? 'phantom' : (selected ? 'selected' : 'default')})`}
      />
      
      <EdgeLabelRenderer>
        {/* Quantity Label */}
        <div
          className={`edge-label-container ${selected ? 'selected' : ''} ${isPhantomPassthrough ? 'phantom' : ''}`}
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'all',
          }}
        >
          {isEditing ? (
            <input
              type="number"
              className="quantity-input"
              value={tempQuantity}
              onChange={(e) => setTempQuantity(e.target.value)}
              onBlur={handleQuantitySubmit}
              onKeyDown={handleKeyDown}
              autoFocus
              step="0.000001"
              min="0"
            />
          ) : (
            <div 
              className="quantity-label"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
                setTempQuantity(data?.quantity || 1);
              }}
              title="Click to edit quantity"
            >
              <span className="qty-value">{formatQuantity(data?.quantity)}</span>
              <span className="qty-unit">x</span>
            </div>
          )}
          
          {/* Delete button on hover/selected */}
          {selected && !isEditing && (
            <button
              className="edge-delete-btn"
              onClick={handleDelete}
              title="Delete connection"
            >
              x
            </button>
          )}
          
          {/* Phantom indicator */}
          {isPhantomPassthrough && (
            <div className="phantom-badge" title="Components pass through (phantom)">
              P
            </div>
          )}
        </div>
      </EdgeLabelRenderer>

      {/* Arrow marker definitions */}
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <marker
            id="arrow-default"
            markerWidth="12"
            markerHeight="12"
            viewBox="-10 -10 20 20"
            refX="0"
            refY="0"
            orient="auto"
          >
            <polyline
              points="-5,-4 0,0 -5,4"
              fill="none"
              stroke="#475569"
              strokeWidth="1.5"
            />
          </marker>
          <marker
            id="arrow-selected"
            markerWidth="12"
            markerHeight="12"
            viewBox="-10 -10 20 20"
            refX="0"
            refY="0"
            orient="auto"
          >
            <polyline
              points="-5,-4 0,0 -5,4"
              fill="none"
              stroke="#60a5fa"
              strokeWidth="1.5"
            />
          </marker>
          <marker
            id="arrow-phantom"
            markerWidth="12"
            markerHeight="12"
            viewBox="-10 -10 20 20"
            refX="0"
            refY="0"
            orient="auto"
          >
            <polyline
              points="-5,-4 0,0 -5,4"
              fill="none"
              stroke="#f59e0b"
              strokeWidth="1.5"
            />
          </marker>
        </defs>
      </svg>
    </>
  );
};

export default BOMEdge;